import { Injectable, inject, signal } from '@angular/core';

import { HtmlNode } from '../browser/html-node.service';
import { Page } from '../client-config/page.client-config';
import { LoadingIndicatorHandler } from './loading-indicator-handler';
import { LoadingIndicatorOptions } from './loading-indicator.models';

/**
 * @whatItDoes Controls the global loading indicator
 *
 * @howToUse
 *
 * ```
 * var loadingIndicator = LoadingIndicatorService.start();
 *
 * loadingIndicator.done();
 * ```
 *
 * @description
 *
 * This service controls when to show and hide the global loading indicator.
 *
 * To close, same number of `done` calls is required as then number of `start` calls.
 * You can only call `done` once on each instance created by `start`.
 *
 * Optionally you can specify delay before the timer is shown (default is 250ms).
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LoadingIndicatorService {
    readonly visible = signal<boolean>(false);

    private readonly htmlNode = inject(HtmlNode);
    private readonly page = inject(Page);

    private counter: number = 0;
    private blockScrollCounter: number = 0;
    private readonly disabledUrlPattern: RegExp = new RegExp(this.page.loadingIndicator.disabledUrlPattern ?? '(?!)', 'i');
    private readonly defaultOptions: LoadingIndicatorOptions = { delay: this.page.loadingIndicator.defaultDelay };

    start(options?: LoadingIndicatorOptions): LoadingIndicatorHandler {
        const opts = Object.assign({}, this.defaultOptions, options);

        this.counter++;
        this.handleBlockScrolling(opts, true);

        if (opts.disabled === undefined && opts?.url) {
            opts.disabled = this.disabledUrlPattern.test(opts.url);
        }

        const handler = new LoadingIndicatorHandler(opts);

        handler.show.subscribe((isVisible: boolean) => this.handleVisibilityChange(isVisible, opts));

        return handler;
    }

    private handleBlockScrolling(opts: LoadingIndicatorOptions, enable: boolean) {
        if (opts.blockScrolling) {
            this.blockScrollCounter += enable ? 1 : -1;
            this.htmlNode.blockScrolling(enable);
        }
    }

    private handleVisibilityChange(isVisible: boolean, opts: LoadingIndicatorOptions) {
        if (isVisible) {
            this.visible.set(true);
            return;
        }

        this.counter--;
        this.handleBlockScrolling(opts, false);

        if (this.counter === 0) {
            this.visible.set(false);
        }
    }
}
